import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { RouterStateService } from './router-state.service';

declare const Intercom: any;

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  constructor(private routerState: RouterStateService, private authService: AuthService) {
    this.listenForLoggedIn();
    this.listenToRouterStateChanges();
  }

  public show() {
    if (hasIntercom()) {
      Intercom('show');
    }
  }

  public showMessages() {
    if (hasIntercom()) {
      Intercom('showMessages');
    }
  }

  public showNewMessage() {
    if (hasIntercom()) {
      Intercom('showNewMessage');
    }
  }

  private listenToRouterStateChanges() {
    this.routerState.routerState$
      .pipe(
        map((state) => state.url),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        if (hasIntercom()) {
          Intercom('update');
        }
      });
  }

  private listenForLoggedIn() {
    this.authService.isLoggedIn$.pipe(distinctUntilChanged()).subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        this.boot();
      } else {
        this.shutdown();
      }
    });
  }

  private async boot() {
    const user = await this.authService.userInfo$
      .pipe(
        filter((u) => !!u && !!u.id),
        take(1),
      )
      .toPromise();
    if (user && user.id) {
      boot(environment.intercomId);
      if (hasIntercom()) {
        try {
          const intercomSettings = {
            app_id: environment.intercomId,
            name: user.driverName,
            phone: user.phoneNumber,
            subcontractor_id: user.companyId,
            subcontractor_name: user.companyName,
            user_id: user.id,
            user_permissions: user.permissions,
            created_at: new Date(user.verifiedTimestamp),
            lohiRelationship: user.accountRelationship,
            hasDispatcher: user.hasDispatcher,
            Appname: 'Lohi', // Add this line to set the app name
          };
          Intercom('boot', intercomSettings);
        } catch (e) {
          console.error(e);
        }
      }
    }
  }

  public shutdown() {
    if (hasIntercom()) {
      Intercom('shutdown');
    }
  }
}

function hasIntercom() {
  return typeof Intercom !== 'undefined';
}

function boot(appID: string) {
  const w = window;
  // @ts-ignore
  const ic = w.Intercom as any;
  if (typeof ic === 'function') {
    ic('reattach_activator');
    // @ts-ignore
    ic('update', w.intercomSettings);
  } else {
    const d = document;
    const i = () => {
      i.c(arguments);
    };
    i.q = [];
    i.c = (args) => {
      i.q.push(args);
    };
    // @ts-ignore
    w.Intercom = i;
    const l = () => {
      const s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = `https://widget.intercom.io/widget/${appID}`;
      const x = d.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    };
    if (document.readyState === 'complete') {
      l();
    } else {
      // @ts-ignore
      if (w.attachEvent) {
        // @ts-ignore
        w.attachEvent('onload', l);
      } else {
        w.addEventListener('load', l, false);
      }
    }
  }
}
